import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';
import type { Instance as TippyInstance } from 'tippy.js';
import { MentionList } from './MentionList';
import api, { getCurrentStytchJWT } from '../../services/api';

/**
 * Render mention suggestions for mindmaps and nodes
 * Triggered when user types '@' in the chat composer
 */
export const renderMentionSuggestion = () => {
  return {
    char: '@',
    
    items: async ({ query }: { query: string }) => {
      try {
        const results: any[] = [];
        const searchQuery = query.toLowerCase().trim();
        
        // Get auth token
        const token = getCurrentStytchJWT();
        if (!token) {
          console.warn('No auth token available for mention search');
          return [];
        }
        
        const headers = { Authorization: `Bearer ${token}` };
        const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
        
        // Fetch mindmaps from backend
        try {
          const mindmapsResponse = await api.get(`${backendUrl}/mindmaps`, { headers });
          const mindmaps = mindmapsResponse.data || [];
          
          // Filter mindmaps by query
          const matchingMindmaps = mindmaps
            .filter((m: any) => 
              !searchQuery || m.name?.toLowerCase().includes(searchQuery) || m.description?.toLowerCase().includes(searchQuery)
            )
            .slice(0, 8)
            .map((m: any) => ({
              id: m.id,
              label: m.name || 'Untitled Mindmap',
              subtitle: m.description || 'Mindmap',
              icon: '🗺️',
              type: 'mindmap',
              metadata: { 
                entityType: 'mindmap',
                description: m.description,
                created_at: m.createdAt,
              },
            }));
          
          results.push(...matchingMindmaps);
        } catch (error) {
          console.error('Failed to fetch mindmaps:', error);
        }
        
        // Fetch nodes from backend (get all mindmaps and extract nodes)
        try {
          const mindmapsResponse = await api.get(`${backendUrl}/mindmaps`, { headers });
          const mindmaps = mindmapsResponse.data || [];
          
          // Collect all nodes from all mindmaps
          const allNodes: any[] = [];
          for (const mindmap of mindmaps) {
            if (mindmap.nodes && Array.isArray(mindmap.nodes)) {
              allNodes.push(...mindmap.nodes.map((node: any) => ({
                ...node,
                mindmapId: mindmap.id,
                mindmapName: mindmap.name
              })));
            }
          }
          
          // Filter nodes by query
          const matchingNodes = allNodes
            .filter((n: any) => 
              !searchQuery || n.label?.toLowerCase().includes(searchQuery) || n.data?.description?.toLowerCase().includes(searchQuery)
            )
            .slice(0, 12)
            .map((n: any) => ({
              id: n.id,
              label: n.label || 'Untitled Node',
              subtitle: `Node • ${n.mindmapName || 'Mindmap'}`,
              icon: '📍',
              type: 'node',
              metadata: { 
                entityType: 'node',
                nodeType: n.type,
                mindmapId: n.mindmapId,
                description: n.data?.description,
              },
            }));
          
          results.push(...matchingNodes);
        } catch (error) {
          console.error('Failed to fetch nodes:', error);
        }
        
        // If no query, show placeholders
        if (!searchQuery && results.length === 0) {
          return [
            { id: 'hint-mindmaps', label: 'Start typing to search mindmaps...', type: 'hint', icon: '🗺️', disabled: true },
            { id: 'hint-nodes', label: 'Or search nodes...', type: 'hint', icon: '📍', disabled: true },
          ];
        }
        
        return results;
      } catch (error) {
        console.error('Failed to fetch mentions:', error);
        return [];
      }
    },

    render: () => {
      let component: ReactRenderer;
      let popup: TippyInstance[];

      return {
        onStart: (props: any) => {
          component = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
          });

          if (!props.clientRect) {
            return;
          }

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect as any,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            maxWidth: '400px',
          });
        },

        onUpdate(props: any) {
          component.updateProps(props);

          if (!props.clientRect) {
            return;
          }

          popup[0].setProps({
            getReferenceClientRect: props.clientRect as any,
          });
        },

        onKeyDown(props: any) {
          if (props.event.key === 'Escape') {
            popup[0].hide();
            return true;
          }

          return component.ref?.onKeyDown(props);
        },

        onExit() {
          popup[0].destroy();
          component.destroy();
        },
      };
    },
  };
};


